import { useState } from "react";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { OrderStatus, SellerOrderListItem } from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";
import { formatCurrency } from "@/lib/formatting/currency";
import { useOrdersQuery, type OrdersQueryPaymentMethod, type OrdersQuerySort } from "@/modules/orders/services/useOrdersQuery";
import { useOrderStatusCountsQuery } from "@/modules/orders/services/useOrderStatusCountsQuery";
import { getStatusMeta } from "@/modules/orders/lib/order-status-meta";
import {
  buildReasonGroups,
  waitingOnBuyerCount,
  type StatusCountMap,
} from "@/modules/orders/lib/orders-attention-model";
import { fulVisual } from "@/modules/orders/components/order-row/order-visuals";
import { ImageThumb } from "@/shared/components/PlaceholderImage";
import { getOrderItemImageUrl } from "@/lib/order-image";
import { pickLocalized } from "@/lib/i18n/localized";
import { useThemeStore } from "@/shared/theme/useTheme";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { MobileOrderCard } from "./orders-smart/MobileOrderCard";
import { OrderActionButton } from "./order-row/OrderActionButton";

/** First-item product title, localized (mirrors the table's item title). */
function productTitle(order: SellerOrderListItem, locale: Locale): string {
  const first = order.items[0];
  if (!first) return "—";
  return (
    pickLocalized(
      locale,
      first.resolvedTitleAr || first.productNameAr,
      first.resolvedTitleEn || first.productNameEn,
    ) ||
    first.resolvedTitleAr ||
    first.productNameAr
  );
}

function pickTheme<T>(isDark: boolean, light: T, dark: T): T {
  return isDark ? dark : light;
}

/** Action-button tint for a status (design tonePair): red for payment/refund,
 * violet for confirm/fulfil, amber otherwise. The group marker dot uses the
 * shared fulVisual palette instead, so it stays in sync with the row rings. */
function attTone(
  status: OrderStatus,
  isDark: boolean,
): { btnBg: string; btnFg: string } {
  switch (status) {
    case "declined":
    case "cancelled":
    case "paid_less_than_required":
    case "pending_payment_verification":
      return {
        btnBg: pickTheme(isDark, "#FCEDED", "rgba(239,68,68,.16)"),
        btnFg: pickTheme(isDark, "#DC2626", "#F87171"),
      };
    case "pending_seller_confirmation":
    case "confirmed":
      return {
        btnBg: pickTheme(isDark, "#FBF1E3", "rgba(245,158,11,.16)"),
        btnFg: pickTheme(isDark, "#B45309", "#FBBF6B"),
      };
    default:
      return {
        btnBg: pickTheme(isDark, "#FBF1E3", "rgba(245,158,11,.16)"),
        btnFg: pickTheme(isDark, "#B45309", "#FBBF6B"),
      };
  }
}

/** A card section: a coloured marker dot, an uppercase label, and a count. */
type Section = {
  key: string;
  labelKey: string;
  dot: string;
  count: number;
  orders: SellerOrderListItem[];
};

type Props = {
  storeSlug: string;
  paymentMethod?: OrdersQueryPaymentMethod;
  sort?: OrdersQuerySort;
  onSelectOrder: (id: string) => void;
  onQuickAction: (order: SellerOrderListItem, targetStatus: OrderStatus) => void;
  onOpenQuickSheet: (order: SellerOrderListItem) => void;
  quickActionBusyId: string | null;
};

/**
 * The always-on action-first area (Trioty design): a "Needs attention" card
 * whose orders are grouped by next action (Review payment / Confirm order),
 * urgency-ordered, with server-truth counts; plus a quiet "Waiting on buyer"
 * card for orders where the ball is in the buyer's court. Falls back to an
 * "All caught up" card when no seller action is pending.
 */
export function OrdersNeedsAttention({
  storeSlug,
  paymentMethod,
  sort,
  onSelectOrder,
  onQuickAction,
  onOpenQuickSheet,
  quickActionBusyId,
}: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const isDark = useThemeStore((s) => s.resolvedDark);
  const isMobile = useIsMobile();
  const [openAttention, setOpenAttention] = useState(true);
  const [openWaiting, setOpenWaiting] = useState(false);

  const { data } = useOrdersQuery({
    storeSlug,
    bucket: "needs_attention",
    limit: 30,
    paymentMethod,
    sort,
  });
  const items = data?.items ?? [];

  const { data: waitingData } = useOrdersQuery({
    storeSlug,
    bucket: "waiting_on_buyer",
    limit: 30,
    sort,
  });
  const waitingItems = waitingData?.items ?? [];

  // Store-wide per-status counts → server-truth section/group totals.
  const { data: countsData } = useOrderStatusCountsQuery({ storeSlug });
  const counts: StatusCountMap | null = countsData?.counts ?? null;
  const groupCount = (statuses: OrderStatus[], loaded: number) =>
    counts ? statuses.reduce((n, s) => n + (counts[s] ?? 0), 0) : loaded;

  // Attention → reason groups (urgency-ordered by the shared model).
  const attentionSections: Section[] = buildReasonGroups(items).map((g) => ({
    key: g.key,
    labelKey: g.labelKey,
    dot: fulVisual(g.sampleStatus, isDark).color,
    count: groupCount(g.statuses, g.orders.length),
    orders: g.orders,
  }));
  const attentionTotal = attentionSections.reduce((n, s) => n + s.count, 0);
  const waitingTotal = counts ? waitingOnBuyerCount(counts) : waitingItems.length;

  const runAction = (order: SellerOrderListItem, target: OrderStatus) => {
    const meta = getStatusMeta(order.status);
    if (meta.transition && (meta.transition.needsEta || meta.transition.requiresDeclineReason)) {
      onOpenQuickSheet(order);
    } else {
      onQuickAction(order, target);
    }
  };

  // One order row — mobile card or desktop row — shared by both cards.
  const renderOrderRow = (order: SellerOrderListItem) => {
    const meta = getStatusMeta(order.status);
    if (isMobile) {
      return (
        <div key={order.id} className="px-3 pb-3 first:pt-3">
          <MobileOrderCard
            order={order}
            onSelect={() => onSelectOrder(order.id)}
            action={
              meta.transition ? (
                <OrderActionButton
                  order={order}
                  onQuickAction={onQuickAction}
                  onOpenQuickSheet={onOpenQuickSheet}
                  onSelect={() => onSelectOrder(order.id)}
                  busy={quickActionBusyId === order.id}
                />
              ) : undefined
            }
          />
        </div>
      );
    }
    const first = order.items[0] ?? null;
    const extra = order.itemCount - 1;
    const tone = attTone(order.status, isDark);
    return (
      <div
        key={order.id}
        role="button"
        tabIndex={0}
        onClick={() => onSelectOrder(order.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onSelectOrder(order.id);
        }}
        className="flex cursor-pointer items-center gap-[13px] border-t border-[color:var(--hair)] px-[18px] py-3 transition-colors hover:bg-[color:var(--soft)]"
      >
        <ImageThumb
          src={first ? getOrderItemImageUrl(first) : null}
          seed={first?.productId}
          alt={productTitle(order, locale)}
          size="h-[42px] w-[42px]"
          bg="bg-white"
          className="rounded-[10px]"
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-bold text-[color:var(--text-primary)]">
            {order.customer.name || "—"}{" "}
            <span className="text-[11.5px] font-semibold text-[color:var(--text-disabled)]">
              #{order.orderCode}
            </span>
          </div>
          <div className="mt-0.5 truncate text-[11.5px] text-[color:var(--text-muted)]">
            {productTitle(order, locale)}
            {extra > 0 ? (
              <span className="text-[color:var(--text-disabled)]">
                {" "}
                {t(keys.orders.rowMoreItems, { count: extra })}
              </span>
            ) : null}
          </div>
          {meta.hintKey ? (
            <div className="mt-[3px] truncate text-[11.5px] text-[color:var(--text-disabled)]">
              {t(meta.hintKey)}
            </div>
          ) : null}
        </div>
        <div className="shrink-0 text-end">
          <div className="text-[13.5px] font-bold text-[color:var(--text-primary)]">
            {formatCurrency(order.totalAmount, locale)}
          </div>
          <div className="mt-0.5 text-[11.5px] text-[color:var(--text-muted)]">
            {order.paymentMethod === "COD"
              ? t(keys.orders.paymentMethodCod)
              : t(keys.orders.paymentMethodExternalOnline)}
          </div>
        </div>
        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          {meta.transition ? (
            <button
              type="button"
              onClick={() => runAction(order, meta.transition!.targetStatus)}
              disabled={quickActionBusyId === order.id}
              className="inline-flex h-[36px] w-[124px] items-center justify-center whitespace-nowrap rounded-[9px] border border-transparent px-2 text-[12.5px] font-bold transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ background: tone.btnBg, color: tone.btnFg }}
            >
              {quickActionBusyId === order.id ? (
                <svg aria-hidden="true" className="h-[14px] w-[14px] animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeDashoffset="12" />
                </svg>
              ) : (
                t(meta.nextLabelKey)
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onSelectOrder(order.id)}
              className="inline-flex h-[36px] w-[124px] items-center justify-center whitespace-nowrap rounded-[9px] border border-[color:var(--border-strong)] bg-[color:var(--card)] px-2 text-[12.5px] font-bold text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
            >
              {t(keys.orders.metaNextViewOrder)}
            </button>
          )}
        </div>
      </div>
    );
  };

  const attnHead = {
    bg: pickTheme(isDark, "#FBF1E3", "rgba(245,158,11,.16)"),
    fg: pickTheme(isDark, "#B45309", "#FBBF6B"),
  };

  const caughtUp = items.length === 0;

  return (
    <>
      {caughtUp ? (
        <div className="mb-[18px] flex items-center gap-[13px] rounded-[18px] border border-[color:var(--card-br)] bg-[color:var(--card)] px-[18px] py-4">
          <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[11px] bg-[color:var(--success-100)] text-[color:var(--success-500)]">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </span>
          <div>
            <div className="text-[14px] font-bold text-[color:var(--text-primary)]">
              {t(keys.orders.attentionCaughtUpTitle)}
            </div>
            <div className="mt-0.5 text-[12.5px] text-[color:var(--text-muted)]">
              {t(keys.orders.attentionCaughtUpSub)}
            </div>
          </div>
        </div>
      ) : (
        <SectionCard
          open={openAttention}
          onToggle={() => setOpenAttention((v) => !v)}
          headBg={attnHead.bg}
          headFg={attnHead.fg}
          icon={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m10.3 3.2-8 14A2 2 0 0 0 4 20h16a2 2 0 0 0 1.7-2.8l-8-14a2 2 0 0 0-3.4 0Z" />
              <path d="M12 9v4M12 17h.01" />
            </svg>
          }
          title={t(keys.orders.smartBucketNeedsAttention)}
          badge={attentionTotal}
          subtitle={t(keys.orders.attentionSubtitle)}
          sections={attentionSections}
          renderOrderRow={renderOrderRow}
          t={t}
        />
      )}

      {waitingItems.length > 0 ? (
        <SectionCard
          open={openWaiting}
          onToggle={() => setOpenWaiting((v) => !v)}
          headBg={pickTheme(isDark, "#EEF1F5", "rgba(148,163,184,.16)")}
          headFg={pickTheme(isDark, "#475569", "#94A3B8")}
          icon={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
          }
          title={t(keys.orders.waitingOnBuyerSectionLabel)}
          badge={waitingTotal}
          sections={[
            {
              key: "waiting",
              labelKey: keys.orders.waitingOnBuyerSectionLabel,
              dot: pickTheme(isDark, "#64748b", "#94A3B8"),
              count: waitingTotal,
              orders: waitingItems,
            },
          ]}
          renderOrderRow={renderOrderRow}
          t={t}
        />
      ) : null}
    </>
  );
}

/** A collapsible action-first card: header (icon + title + count) → sections
 *  (marker dot + label + count) → order rows. */
function SectionCard({
  open,
  onToggle,
  headBg,
  headFg,
  icon,
  title,
  badge,
  subtitle,
  sections,
  renderOrderRow,
  t,
}: {
  open: boolean;
  onToggle: () => void;
  headBg: string;
  headFg: string;
  icon: React.ReactNode;
  title: string;
  badge: number;
  subtitle?: string;
  sections: Section[];
  renderOrderRow: (order: SellerOrderListItem) => React.ReactNode;
  t: ReturnType<typeof useTranslations>;
}) {
  return (
    <div className="mb-[18px] overflow-hidden rounded-[18px] border border-[color:var(--card-br)] bg-[color:var(--card)]">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 px-[18px] py-[13px] text-start transition-colors hover:bg-[color:var(--soft)]"
        aria-expanded={open}
      >
        <span className="flex items-center gap-[10px]">
          <span
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[9px]"
            style={{ background: headBg, color: headFg }}
          >
            {icon}
          </span>
          <span className="text-[16px] font-bold text-[color:var(--text-primary)]">{title}</span>
          <span
            className="rounded-full px-[9px] py-0.5 text-[12px] font-bold"
            style={{ background: headBg, color: headFg }}
          >
            {badge}
          </span>
        </span>
        <span className="flex items-center gap-[13px]">
          {subtitle ? (
            <span className="hidden text-[12.5px] font-medium text-[color:var(--text-muted)] sm:inline">
              {subtitle}
            </span>
          ) : null}
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--text-muted)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="transition-transform"
            style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </button>

      {open ? (
        <div className="border-t border-[color:var(--line)]">
          {sections.map((section) => (
            <div key={section.key}>
              <div className="flex items-center gap-2 border-t border-[color:var(--line)] bg-[color:var(--soft)] px-[18px] py-[9px] first:border-t-0">
                <span className="h-[7px] w-[7px] shrink-0 rounded-full" style={{ background: section.dot }} />
                <span className="text-[11px] font-extrabold uppercase tracking-[0.06em] text-[color:var(--text-secondary)]">
                  {t(section.labelKey)}
                </span>
                <span className="text-[11px] font-bold text-[color:var(--text-disabled)]">
                  {section.count}
                </span>
              </div>
              {section.orders.map((order) => renderOrderRow(order))}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
